import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { SupplierSelect } from "../../components/SupplierSelect";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Receipt, Plus, FileText, Download, Search, Upload, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../../lib/api";
import { money, fmtDate, CN_STATUSES } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";

const CURRENCIES = ["OMR", "USD", "AED"];

function cnStatusColor(s) {
  return { UNUSED: "bg-blue-50 text-blue-700", "PARTIALLY USED": "bg-amber-50 text-amber-700", USED: "bg-slate-100 text-slate-600", CANCELLED: "bg-rose-50 text-rose-700" }[s] || "bg-slate-100 text-slate-600";
}

export default function CreditNotes() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const canManage = hasRole("SUPER_ADMIN", "FINANCE");
  const [list, setList] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ cn_number: "", supplier_id: "", issue_date: "", currency: "OMR", original_amount: "", notes: "" });
  const [txnForm, setTxnForm] = useState({ amount: "", date: "", po_reference: "", notes: "" });
  const [confirmTxn, setConfirmTxn] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (supplierFilter) params.set("supplier_id", supplierFilter);
    const { data } = await api.get(`/admin/credit-notes?${params}`);
    setList(data);
  };
  const loadStats = async () => { const { data } = await api.get("/admin/credit-notes/stats"); setStats(data); };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search, statusFilter, supplierFilter]);
  useEffect(() => { loadStats(); }, []);

  const create = async () => {
    if (!form.cn_number || !form.supplier_id || !form.original_amount) { toast.error("CN number, supplier and amount are required"); return; }
    try {
      await api.post("/admin/credit-notes", { ...form, claim_ids: [] });
      toast.success("Credit note created");
      setCreateOpen(false);
      setForm({ cn_number: "", supplier_id: "", issue_date: "", currency: "OMR", original_amount: "", notes: "" });
      load(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (cn) => {
    setDetail({ loading: true });
    const { data } = await api.get(`/admin/credit-notes/${cn.id}`);
    setDetail(data);
  };

  const addTxn = async () => {
    try {
      await api.post(`/admin/credit-notes/${detail.credit_note.id}/transactions`, txnForm);
      toast.success("Usage recorded");
      setTxnForm({ amount: "", date: "", po_reference: "", notes: "" });
      setConfirmTxn(false);
      openDetail(detail.credit_note); load(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setConfirmTxn(false); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/credit-notes/${detail.credit_note.id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF uploaded");
      openDetail(detail.credit_note);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doCancel = async () => {
    try {
      await api.put(`/admin/credit-notes/${detail.credit_note.id}/cancel`);
      toast.success("Credit note cancelled");
      setCancelOpen(false); openDetail(detail.credit_note); load(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STAT_CARDS = stats ? [
    { label: "Total CNs", value: stats.count, icon: Receipt, color: "text-blue-600 bg-blue-50" },
    { label: "Unused", value: stats.unused, icon: FileText, color: "text-blue-600 bg-blue-50" },
    { label: "Partially Used", value: stats.partially_used, icon: FileText, color: "text-amber-600 bg-amber-50" },
    { label: "Used", value: stats.used, icon: FileText, color: "text-slate-600 bg-slate-100" },
  ] : [];

  const currencyCards = stats?.currencies?.filter((c) => c.credit_note_count > 0 || ["OMR", "USD", "AED"].includes(c.currency)) || [];

  return (
    <AdminLayout title="Credit Notes">
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {!stats ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) :
          STAT_CARDS.map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${c.color}`}><c.icon className="h-4 w-4" /></div>
              <div className="font-display text-lg font-bold text-slate-900">{c.value}</div>
              <div className="text-xs text-slate-500">{c.label}</div>
            </div>
          ))}
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {!stats ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />) :
          currencyCards.map((cc) => (
            <div key={cc.currency} data-testid={`cn-currency-card-${cc.currency}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="font-display text-base font-bold tracking-wide text-slate-900">{cc.currency}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{cc.credit_note_count} CN{cc.credit_note_count !== 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Received</span>
                  <span data-testid={`cn-${cc.currency}-received`} className="font-mono text-sm font-semibold text-slate-900">{money(cc.total_received, cc.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Used</span>
                  <span data-testid={`cn-${cc.currency}-used`} className="font-mono text-sm font-semibold text-purple-700">{money(cc.total_used, cc.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Available</span>
                  <span data-testid={`cn-${cc.currency}-available`} className="font-mono text-sm font-semibold text-emerald-700">{money(cc.total_available, cc.currency)}</span>
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="cn-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search CN number..." className="pl-9" />
        </div>
        <div className="flex gap-2">
          {["", ...CN_STATUSES].map((s) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)} className={`rounded-lg px-3 py-1.5 text-xs font-medium ${statusFilter === s ? "bg-blue-600 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <div className="w-full sm:w-64" data-testid="cn-supplier-filter-wrap">
          <SupplierSelect value={supplierFilter} onChange={setSupplierFilter} placeholder="All Suppliers" testId="cn-supplier-filter" />
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)} data-testid="new-cn-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> New Credit Note</Button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>CN Number</TableHead><TableHead>Supplier</TableHead><TableHead>Linked RMA</TableHead><TableHead>Product</TableHead><TableHead>Serial</TableHead><TableHead>Issue Date</TableHead>
              <TableHead>Original</TableHead><TableHead>Used</TableHead><TableHead>Available</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list === null && Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
              {list?.map((cn) => {
                const first = cn.linked_claims && cn.linked_claims[0];
                return (
                <TableRow key={cn.id} data-testid={`cn-row-${cn.cn_number}`} onClick={() => openDetail(cn)} className="cursor-pointer hover:bg-blue-50/50">
                  <TableCell className="font-mono text-sm font-semibold text-blue-700">{cn.cn_number}</TableCell>
                  <TableCell className="text-sm text-slate-700">{cn.supplier_name}</TableCell>
                  <TableCell className="font-mono text-xs">{first ? <button data-testid={`cn-rma-link-${cn.cn_number}`} onClick={(e) => { e.stopPropagation(); navigate(`/admin/warranty/claims/${first.claim_id}`); }} className="font-semibold text-blue-700 hover:underline">{first.rma_number}</button> : <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="max-w-[140px] truncate text-sm text-slate-600">{first?.product_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{first?.serial_number || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(cn.issue_date)}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-700">{money(cn.original_amount, cn.currency)}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">{money(cn.used_amount, cn.currency)}</TableCell>
                  <TableCell className="font-mono text-sm font-semibold text-emerald-700">{money(cn.available_balance, cn.currency)}</TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cnStatusColor(cn.status)}`}>{cn.status}</span></TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
        {list?.length === 0 && <EmptyState icon={Receipt} title="No credit notes" description="Create a credit note when a supplier issues one." testId="cn-empty" />}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Credit Note</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block text-sm font-medium">CN Number *</Label><Input data-testid="cn-number-input" value={form.cn_number} onChange={(e) => setForm({ ...form, cn_number: e.target.value })} placeholder="CN-88213" /></div>
            <div><Label className="mb-1.5 block text-sm font-medium">Supplier *</Label><SupplierSelect value={form.supplier_id} onChange={(id) => setForm({ ...form, supplier_id: id })} testId="cn-supplier-select" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-sm font-medium">Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div><Label className="mb-1.5 block text-sm font-medium">Currency</Label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-sm font-medium">Original Amount *</Label><Input data-testid="cn-amount-input" type="number" value={form.original_amount} onChange={(e) => setForm({ ...form, original_amount: e.target.value })} /></div>
            <div><Label className="mb-1.5 block text-sm font-medium">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-cn-btn" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {detail?.credit_note && (
            <>
              <DialogHeader><DialogTitle className="font-mono">{detail.credit_note.cn_number}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Original" value={money(detail.credit_note.original_amount, detail.credit_note.currency)} />
                <Stat label="Used" value={money(detail.credit_note.used_amount, detail.credit_note.currency)} />
                <Stat label="Available" value={money(detail.credit_note.available_balance, detail.credit_note.currency)} highlight />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3 text-sm">
                <span className="text-slate-500">{detail.credit_note.supplier_name} • Issued {fmtDate(detail.credit_note.issue_date)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cnStatusColor(detail.credit_note.status)}`}>{detail.credit_note.status}</span>
              </div>

              {/* PDF */}
              <div className="flex items-center gap-3">
                {detail.credit_note.pdf_path ? (
                  <a href={fileUrl(detail.credit_note.pdf_path)} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="gap-1.5" data-testid="cn-view-pdf"><FileText className="h-4 w-4" /> View PDF</Button>
                  </a>
                ) : <span className="text-sm text-slate-400">No PDF attached</span>}
                {canManage && (
                  <label className="cursor-pointer">
                    <Button variant="outline" size="sm" className="gap-1.5 pointer-events-none"><Upload className="h-4 w-4" /> Upload PDF</Button>
                    <input type="file" accept=".pdf,image/*" className="hidden" data-testid="cn-pdf-upload" onChange={(e) => uploadPdf(e.target.files[0])} />
                  </label>
                )}
              </div>

              {/* Linked claims */}
              {detail.credit_note.linked_claims?.length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Linked Claims</div>
                  <div className="flex flex-wrap gap-2">
                    {detail.credit_note.linked_claims.map((l) => (
                      <span key={l.claim_id} className="rounded-lg bg-blue-50 px-2 py-1 font-mono text-xs text-blue-700">{l.rma_number}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Usage ledger */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="font-display text-sm font-semibold text-slate-900">Usage Ledger</h4>
                </div>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead>Amount</TableHead><TableHead>Date</TableHead><TableHead>PO Ref</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {detail.transactions.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-mono text-sm font-semibold text-slate-900">{money(t.amount, detail.credit_note.currency)}</TableCell>
                          <TableCell className="text-sm text-slate-600">{fmtDate(t.date)}</TableCell>
                          <TableCell className="text-sm text-slate-600">{t.po_reference || "—"}</TableCell>
                          <TableCell className="text-sm text-slate-500">{t.employee}</TableCell>
                        </TableRow>
                      ))}
                      {detail.transactions.length === 0 && <TableRow><TableCell colSpan={4} className="py-4 text-center text-sm text-slate-400">No usage recorded</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Add usage */}
              {canManage && detail.credit_note.status !== "CANCELLED" && detail.credit_note.status !== "USED" && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Record Usage</div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Input data-testid="txn-amount" type="number" placeholder="Amount" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} />
                    <Input type="date" value={txnForm.date} onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })} />
                    <Input placeholder="PO Reference" value={txnForm.po_reference} onChange={(e) => setTxnForm({ ...txnForm, po_reference: e.target.value })} />
                    <Button onClick={() => { if (!txnForm.amount) { toast.error("Enter amount"); return; } setConfirmTxn(true); }} data-testid="record-usage-btn" className="bg-blue-600 hover:bg-blue-700">Record</Button>
                  </div>
                </div>
              )}

              {canManage && detail.credit_note.status !== "CANCELLED" && (
                <button onClick={() => setCancelOpen(true)} data-testid="cancel-cn-btn" className="flex items-center gap-1.5 text-xs text-rose-600 hover:underline"><Ban className="h-3.5 w-3.5" /> Cancel this credit note</button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirmTxn} onOpenChange={setConfirmTxn} financial title="Confirm Credit Usage"
        description={`Record usage of ${money(txnForm.amount, detail?.credit_note?.currency)} against ${detail?.credit_note?.cn_number}? This cannot be undone.`}
        confirmLabel="Record Usage" confirmWord="CONFIRM" onConfirm={addTxn} testId="confirm-txn" />
      <ConfirmDialog open={cancelOpen} onOpenChange={setCancelOpen} destructive financial title="Cancel Credit Note"
        description="Cancelling will void this credit note. Usage history is preserved but no further usage is allowed." confirmLabel="Cancel CN" confirmWord="CANCEL" onConfirm={doCancel} testId="confirm-cancel-cn" />
    </AdminLayout>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`mt-0.5 font-display text-base font-bold ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
